"use client";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  List,
  ListItem,
  FormHelperText,
  useMediaQuery,
} from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import {
  OrderDataProps,
  OrderDataItem as OrderDataItemType,
} from "../lib/type";
import { WebShopIcon } from "../../WebShopIcon";
import { useContextStore } from "@/src/stores/useContextStore";
import useToggle from "@/src/hooks/useToggle";
import {
  Form,
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { cn } from "@/src/shared/utils/mergeTwClasses";
import { cloneDeep, isEmpty, isNil, isString } from "lodash";
import {
  ParsedValue,
  parseVariableToString,
} from "@/src/components/TextWithValue";
import { Modal } from "@/src/components/Modal";
import {BreakPointsPx, PROCESS_INSTANCE_QUERY} from "@/src/shared/const";
import { parseContextVariableValue } from "@/src/shared/utils/dataTypeHelpers";
import { useSearchParams } from "next/navigation";
import { updateProcessInstanceField } from "@/src/api/server-actions/processInstance";
import { useStepsNavigation } from "../../pages/StepPage/components/apiCalls/useStepsNavigation";
import { prepareValueByMask} from "@/src/shared/utils/mask";

const isItemVisible = (
  item: Omit<OrderDataItemType, "context"> & {
    context?: Array<{ name: any; value: any }>;
  },
) => {
  if (!item.hideIfEmpty) {
    return true;
  }

  const isAllItemsEmpty = (item.context || []).every(({ value }) => !value);
  return !isAllItemsEmpty;
};

const useRequiredFormField = (fieldName: string) => {
  const { register, watch, formState, trigger } = useFormContext();
  const value = watch(fieldName);

  useEffect(() => {
    if (!isEmpty(value)) {
      trigger(fieldName);
    }
  }, [value]);

  const invisibleInput = (
    <input
      className="hidden"
      {...register(fieldName, {
        required: "Це поле обов’язкове",
        shouldUnregister: true,
      })}
    />
  );

  return invisibleInput;
};

export const OrderData: FC<OrderDataProps> = ({
  title,
  subtitle,
  hideName,
  items,
  hidden,
  className,
  ...rest
}) => {
  const searchParams = useSearchParams();
  const processInstanceId = searchParams.get(PROCESS_INSTANCE_QUERY);
  const getContextVariable = useContextStore((state) => state.getVariable);
  const [beforeEditData, setBeforeEditData] = useState<any>(null);
  const { goToStep } = useStepsNavigation();
  const modalForm = useFormContext();
  const { trigger, watch, getValues, setValue } = modalForm;
  const requiredField = useRequiredFormField("startDate");
  const valuesForm = watch();

  const updateField = (field: string, value: any) => {
    const variable = getContextVariable(field);
    const variableId = variable?.id;

    const currentValue = parseContextVariableValue(value);

    if (!isString(currentValue) || !processInstanceId || !variableId) return;

    try {
      updateProcessInstanceField({
        processInstanceId,
        variableId,
        value: currentValue,
      });
    } catch (e) {
      console.error(e);
    }
  };

  const updateVariableValue = useContextStore(
    (state) => state.updateVariableValue,
  );
  const {
    open: openEditModal,
    close: closeEditModal,
    isToggled: isEditModalOpen,
  } = useToggle({ initialState: false });

  const [selectedField, setSelectedField] = useState<OrderDataItemType | null>(
    null,
  );

  const handleEditClick = (item: OrderDataItemType) => {
    if (item.stepId) {
      goToStep(item.stepId, "PREVIOUS");
      return;
    }
    const clonedData = cloneDeep(getValues());
    setBeforeEditData(clonedData);
    openEditModal();
    setSelectedField(item);
  };

  const handleClose = () => {
    modalForm.reset(beforeEditData);
    closeEditModal();
  };

  const handleSaveClick = async (_: any) => {
    const formData = getValues();
    const isFormValid = (
      await Promise.all(
        selectedField?.context?.map((contextVar) => trigger(contextVar)) || [],
      )
    ).every((res) => res);

    if (!isFormValid) {
      return;
    }
    [...(selectedField?.context || []), "startDate", "endDate"]?.forEach(
      (contextVar) => {
        updateVariableValue(contextVar, formData[contextVar]);
        updateField(contextVar, formData[contextVar]);
        setValue(contextVar, formData[contextVar], { shouldDirty: true });
      },
    );
    closeEditModal();
  };

  const values = useMemo(() => {
    return items?.map((item) => ({
      ...item,
      context: item.context?.map((contextVar) => {
        const variable = valuesForm[contextVar];
        const value = parseVariableToString(variable, contextVar);
        return {
          name: contextVar,
          value: prepareValueByMask(value, item.maskType),
          placehodler: item?.placeholder,
        };
      }),
    }));
  }, [valuesForm, items]);

  if (hidden) return null;

  // const isTourism = getValues("flowCode") === "VZK_NEW";

  return (
    <Box className={cn("bg-white-gray p-4", className)} {...rest}>
      {requiredField}
      <Stack className="flex flex-col items-start gap-4">
        <Box className="flex flex-col gap-1">
          {!hideName && (
            <Typography
              variant="body1"
              component="h3"
              className="text-lg font-semibold"
            >
              <ParsedValue value={title || ""} />
            </Typography>
          )}
          {subtitle && (
            <Typography component="h5" className="text-sm font-normal">
              <ParsedValue value={subtitle || ""} />
            </Typography>
          )}
        </Box>
        <List
          className={cn(
            "flex flex-col gap-3 max-md:w-full md:grid",
            "md:grid-cols-[max-content_auto]",
          )}
        >
          {values?.map(
            (item, index) =>
              isItemVisible(item) && (
                <ListItem
                  className="relative contents gap-2 max-md:justify-between max-md:flex-center-row"
                  key={item.name ?? index}
                >
                  <OrderDataItem
                    {...item}
                    handleEditClick={handleEditClick}
                    handleSaveClick={handleSaveClick}
                    index={index}
                    closeEditModal={handleClose}
                    selectedField={selectedField}
                    isEditModalOpen={isEditModalOpen}
                    modalForm={modalForm}
                  />
                </ListItem>
              ),
          )}
        </List>
      </Stack>
    </Box>
  );
};

const checkEmptyValue = (value: any) => {
  if (isNil(value)) {
    return true;
  }
  if (typeof value === "string") {
    return value.trim() === "";
  }
  return false;
};

const OrderDataItem: FC<
  Omit<OrderDataItemType, "context"> & {
    context?: { name: string; value: string; placehodler?: string }[];
    modalForm: any;
    isEditModalOpen?: boolean;
    selectedField: any;
    index: number;
    handleEditClick: (args: any) => void;
    closeEditModal: () => void;
    handleSaveClick: (formData: any) => void;
  }
> = ({
  handleEditClick,
  closeEditModal,
  handleSaveClick,
  index,
  selectedField,
  isEditModalOpen,
  modalForm,
  ...item
}) => {
  const isMobile = useMediaQuery(`(max-width:${BreakPointsPx.md})`);
  const { name, context, contextSubText, editable, contextSeparator } = item;
  const { getFieldState, control } = useFormContext();
  const errors = context
    ?.map(({ name }) => getFieldState(name)?.error?.message)
    .filter(Boolean);

  const isValueAvailable = context?.some(
    ({ value }) => !checkEmptyValue(value),
  );
  const value = context?.map(({ value }) => value)?.join(" ");
  const placehodler = context?.map(({ placehodler }) => placehodler)?.join(" ");

  return (
    <>
      <>
        <FormHelperText>
          <Typography className="text relative hidden text-nowrap text-dark-gray md:inline">
            <ParsedValue value={name || ""} maskType={"thousandSeparator"} />:
            {!!errors?.length && (
              <div className="absolute -bottom-3 left-0 text-xs text-red">
                {errors}
              </div>
            )}
          </Typography>
        </FormHelperText>
        <div className="hidden flex-row gap-2 md:flex">
          <Stack
            className={cn("flex flex-row font-semibold text-dark-gray", {
              "gap-1": !contextSeparator,
            })}
          >
            {context?.map((contextVar, contextIndex) => {
              if (
                checkEmptyValue(contextVar?.value) &&
                contextVar?.placehodler
              ) {
                return (
                  <Typography
                    key={contextVar.name}
                    className="text-base font-semibold text-bulish-gray"
                  >
                    {contextIndex > 0 && contextSeparator}
                    {contextVar.placehodler}
                  </Typography>
                );
              }
              return (
                <Typography
                  key={contextVar.name}
                  className="text-base font-semibold text-dark-gray"
                >
                  {contextIndex > 0 && contextSeparator}
                  <span dangerouslySetInnerHTML={{ __html: contextVar?.value }} />
                </Typography>
              );
            })}
            {contextSubText}
          </Stack>
          {editable && !isMobile && (
            <FormProvider {...modalForm}>
              <Modal
                id={name ?? "" + context?.join("-") ?? "" + index}
                onSubmitClick={() => handleSaveClick(modalForm.getValues())}
                open={isEditModalOpen && selectedField?.name === item.name}
                onClose={closeEditModal}
                withoutCloseOnSubmit
              >
                <IconButton
                  className="flex p-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditClick({
                      ...item,
                      context: item.context?.map(({ name }) => name) || [],
                    });
                  }}
                >
                  <WebShopIcon name="pencil" fill={"#009DDE"} />
                </IconButton>
              </Modal>
            </FormProvider>
          )}
        </div>
      </>
      <div className="flex w-full justify-between gap-6 md:hidden">
        <Stack className="relative gap-6 flex-center-row max-md:flex-col max-md:items-start max-md:gap-1">
          <Typography className="text relative text-nowrap text-dark-gray">
            <ParsedValue value={name || ""} maskType={"thousandSeparator"} />:
            {!!errors?.length && (
              <div className="absolute -bottom-3 left-0 text-xs text-red">
                {errors}
              </div>
            )}
          </Typography>
          <Stack
            className={cn(
              "font-semibold text-dark-gray flex-center-row max-md:justify-between",
              { "gap-1": !contextSeparator },
            )}
          >
            {context?.map((contextVar, contextIndex) => {
              if (
                checkEmptyValue(contextVar?.value) &&
                contextVar?.placehodler
              ) {
                return (
                  <Typography
                    key={contextVar.name}
                    className="text-base font-semibold text-bulish-gray"
                  >
                    {contextIndex > 0 && contextSeparator}
                    {contextVar.placehodler}
                  </Typography>
                );
              }
              return (
                <Typography
                  key={contextVar.name}
                  className="text-base font-semibold text-dark-gray"
                >
                  {contextIndex > 0 && contextSeparator}
                  <span dangerouslySetInnerHTML={{ __html: contextVar?.value }} />
                </Typography>
              );
            })}

            {contextSubText}
          </Stack>
        </Stack>
        {editable && isMobile && (
          <FormProvider {...modalForm}>
            <Modal
              id={name ?? "" + context?.join("-") ?? "" + index}
              onSubmitClick={handleSaveClick}
              open={isEditModalOpen && selectedField?.name === item.name}
              onClose={closeEditModal}
              withoutCloseOnSubmit
            >
              <IconButton
                className="flex p-1"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditClick({
                    ...item,
                    context: item.context?.map(({ name }) => name) || [],
                  });
                }}
              >
                <WebShopIcon name="pencil" fill={"#009DDE"} />
              </IconButton>
            </Modal>
          </FormProvider>
        )}
      </div>
    </>
  );
};
